import React from 'react';
import { Field } from 'react-final-form';
import styled from 'styled-components';
import { ObjectSchema } from 'yup';

import { InputType } from '../../../../types/api';
import { fieldsMap } from '../../_common/fieldsMap';
import { GroupT } from '../types';
import { useGroup } from '../utils/hooks/useGroup';

import CustomerFormField from './CustomerFormField';

const GroupContainer = styled.div<{ direction?: 'row' | 'column' }>`
  display: flex;
  flex-direction: column;
  & > div:not(:first-child) {
    margin: 1rem 0 0 0;
  }

  ${({ theme }) => theme.breakPoints.medium} {
    flex-direction: ${({ direction }) => direction ?? 'row'};
    & > div:not(:first-child) {
      margin: ${({ direction }) =>
        direction === 'column' ? '1rem 0 0 0' : '0 0 0 1rem'};
    }
  }
`;

interface Props {
  group: GroupT;
  validationSchema: ObjectSchema<any>;
  direction?: 'row' | 'column';
}

export default function FieldGroup(props: Props) {
  const { direction, group, validationSchema } = props;
  const { fields = [], icon, label: groupLabel } = group;

  const {
    meta: { error, touched },
    required: groupRequired,
    validate,
    value: groupValue,
  } = useGroup(fields, validationSchema);

  return (
    <CustomerFormField
      error={error}
      icon={icon}
      label={groupLabel}
      renderInputField={(
        startEditing,
        stopEditing,
        startHovering,
        stopHovering,
      ) => (
        <GroupContainer
          direction={direction ?? 'column'}
          onMouseEnter={startHovering}
          onMouseLeave={stopHovering}
        >
          {fields.map(({ id, inputType, options, visible }) => {
            const FieldComponent = fieldsMap[inputType];

            return (
              FieldComponent &&
              visible && (
                <Field
                  key={id}
                  name={id}
                  subscription={{ touched: true, error: true, value: true }}
                  validate={validate[id]}
                  validateFields={[]}
                >
                  {({ input: { onBlur, onChange, onFocus, value } }) => (
                    <FieldComponent
                      key={id}
                      name={id}
                      onBlur={() => {
                        stopEditing();
                        onBlur();
                      }}
                      onChange={onChange}
                      onFocus={() => {
                        startEditing();
                        onFocus();
                      }}
                      options={
                        inputType === InputType.RADIO_BUTTONS && options
                          ? options
                          : []
                      }
                      value={value as any}
                    />
                  )}
                </Field>
              )
            );
          })}
        </GroupContainer>
      )}
      required={groupRequired}
      touched={touched}
      value={groupValue}
      isGroup
    />
  );
}
